import React, { useCallback } from "react";
import { useFormContext, useWatch } from "react-hook-form";

import {
  DateField,
  SelectField,
  TextField,
} from "components/AddVaccineForm/components";
import { Checkbox } from "components/ui/Checkbox";
import { DuplicatePatientNotice } from "features/patients/components/DuplicatePatientNotice";
import { useDuplicatePatientCheck } from "features/patients/hooks/useDuplicatePatientCheck";
import { sexOptions, titleOptions } from "utils/patientFormOptions";

import s from "../PatientProfileForm.module.css";

export function PatientProfilePersonalSection({ isCreate, ethnicGroups }) {
  const { setValue, watch, trigger } = useFormContext();
  const noPpsn = useWatch({ name: "noPpsn" });
  const died = useWatch({ name: "died" });

  const {
    onFieldBlur,
    candidates: duplicateCandidates,
    dismiss: dismissDuplicates,
  } = useDuplicatePatientCheck({ enabled: isCreate });

  const onEthnicDescriptionChange = useCallback(
    (description) => {
      const selected = ethnicGroups.find((g) => g.description === description);
      setValue("ethnicityCode", selected ? selected.code : "");
    },
    [ethnicGroups, setValue]
  );

  const onNoPpsnChange = (e) => {
    const checked = e.target.checked;
    setValue("noPpsn", checked);

    if (checked) {
      setValue("nationalNumber", "");
    }
  };

  const onDiedChange = (e) => {
    const checked = e.target.checked;
    setValue("died", checked);

    if (!checked) {
      setValue("dateOfDeath", "");
      setValue("causeOfDeath", "");
    }
  };

  const sectionClass = isCreate ? s.sectionCreate : s.sectionEdit;

  const ethnicityDescription =
    ethnicGroups.find((g) => g.code === watch("ethnicityCode"))?.description ??
    "";

  return (
    <div className={sectionClass}>
      <div className={s.formGrid4}>
        <SelectField
          name="title"
          label="Title"
          options={titleOptions}
          placeholder="Select"
          onChange={(value) => {
            setValue("title", value);
          }}
        />
        <TextField
          name="firstName"
          label="First Name *"
          placeholder="Enter first name"
          ariaRequired
          onBlur={onFieldBlur}
        />
        <TextField
          name="surname"
          label="Last Name *"
          placeholder="Enter last name"
          ariaRequired
          onBlur={onFieldBlur}
        />
        <TextField
          name="mothersMaidenName"
          label="Mother's Maiden Name"
          placeholder="Enter mother's maiden name"
        />
      </div>
      <div className={`${s.formGrid4} ${s.formGrid4PersonalDemographics}`}>
        <div>
          <TextField
            name="nationalNumber"
            label="PPS Number"
            placeholder={noPpsn ? "No PPSN issued" : "Enter PPS number"}
            disabled={!!noPpsn}
            onBlur={() => !noPpsn && trigger("nationalNumber")}
          />
          <label className={s.noPpsnLabel}>
            <Checkbox checked={!!noPpsn} onChange={onNoPpsnChange} />
            No PPSN issued
          </label>
        </div>
        <div>
          {/* No asterisk, and not aria-required: a patient can be registered without a
              date of birth (KIN-1733). The consequence is stated where it bites —
              nothing can be sent to the HSE for them — rather than by refusing to save
              a record the practice needs now. */}
          <DateField
            name="dateOfBirth"
            label="Date Of Birth"
            placeholder="Select date of birth"
            maxDate={isCreate ? new Date() : undefined}
            onBlur={onFieldBlur}
          />
          <label className={s.noPpsnLabel}>
            <Checkbox checked={!!died} onChange={onDiedChange} />
            Deceased
          </label>
        </div>
        <SelectField
          name="gender"
          label="Sex *"
          options={sexOptions}
          placeholder="Select"
          onChange={(value) => {
            setValue("gender", value);
          }}
          ariaRequired
        />
        <SelectField
          name="ethnicityCode"
          label="Ethnic Group"
          options={ethnicGroups.map((group) => group.description)}
          value={ethnicityDescription}
          placeholder="Select"
          onChange={onEthnicDescriptionChange}
          ariaRequired
        />
      </div>
      {isCreate && (
        <DuplicatePatientNotice
          candidates={duplicateCandidates}
          onDismiss={dismissDuplicates}
        />
      )}
      {!!died && (
        <div className={`${s.formGrid} ${s.formGrid4PersonalDemographics}`}>
          <DateField
            name="dateOfDeath"
            label="Date Of Death"
            placeholder="Select date of death"
            maxDate={isCreate ? new Date() : undefined}
          />
          <TextField
            name="causeOfDeath"
            label="Cause Of Death"
            placeholder="Enter cause of death"
          />
        </div>
      )}
    </div>
  );
}
